import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Stack, Typography } from '@mui/material';
import {
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  useGridApiRef,
} from '@mui/x-data-grid-pro';

import { uniq } from 'lodash-es';
import { generateCsvDownload } from '../../lib/csv';
import { selectors, actions } from '../../store/dux/soil-pending-fields-dux';
import { actions as alertActions } from '../../store/dux/alerts-dux';
import { Spinner } from '../../components/Spinner';
import ActionButton from '../../components/ActionButton';
import { Table, TableDetailToggle } from '../../components/table/Table';
import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog';
import { SelectButton } from '../../components/SelectButton';
import SoilPointsTable from './SoilPointsTable';

const translations = {
  PARTIAL: 'Partial',
  EMAILED: 'Emailed PA',
  RERUN: 'Lab ReRun',
};

const attributes = {
  pBray: { decimals: 0, header: 'P' },
  omPer: { decimals: 1, header: '%OM' },
  cec: { decimals: 0, header: 'CEC' },
  soilPh: { decimals: 1, header: 'pH' },
  kPpm: { decimals: 0, header: 'K' },
  bufferPh: { decimals: 1, header: 'LI' },
  caPpm: { decimals: 0, header: 'Ca' },
  mgPpm: { decimals: 0, header: 'Mg' },
  ctSatH: { decimals: 0, header: 'H SAT' },
  ctSatK: { decimals: 1, header: 'K SAT' },
  ctSatCa: { decimals: 0, header: 'Ca SAT' },
  ctSatMg: { decimals: 0, header: 'Mg SAT' },
  sulphurPpm: { decimals: 1, header: 'S' },
  zincPpm: { decimals: 1, header: 'Zn' },
  bPpm: { decimals: 1, header: 'B' },
  cuPpm: { decimals: 1, header: 'Cu' },
  fePpm: { decimals: 0, header: 'Fe' },
  mnPpm: { decimals: 1, header: 'Mn' },
};

export default function PendingFields() {
  const dispatch = useDispatch();
  const [isConfirmingReject, setIsConfirmingReject] = useState(false);
  const [isConfirmingApprove, setIsConfirmingApprove] = useState(false);
  const isWorking = useSelector(selectors.isWorking);
  const failures = useSelector(selectors.failures);
  const fields = useSelector(selectors.fields);
  const selectedFields = useSelector(selectors.selectedFields);
  const rawDelayReasons = useSelector(selectors.delayReasons);
  const apiRef = useGridApiRef();

  const delayReasons = useMemo(
    () =>
      rawDelayReasons &&
      rawDelayReasons
        .map((reason) => ({
          value: reason.id,
          label: translations[reason.name] || reason.name,
        }))
        .concat({ value: '', label: 'None' }),
    [rawDelayReasons],
  );

  const rejectPoint = useCallback(
    (point) => dispatch(actions.rejectPoint(point)),
    [dispatch],
  );

  const getDetailPanelContent = useCallback(
    ({ row }) => (
      <SoilPointsTable
        {...row}
        attributes={attributes}
        onRejectClick={rejectPoint}
      />
    ),
    [rejectPoint],
  );

  useEffect(() => {
    dispatch(actions.loadFields());
    dispatch(actions.loadDelayReasons());
  }, [dispatch]);

  useEffect(() => {
    dispatch(alertActions.showError(failures?.at(0)));
  }, [failures, dispatch]);

  return (
    <Box
      sx={{ p: 4, display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      {buildRejectConfirmation()}
      {buildApproveConfirmation()}
      {buildHeader()}
      {fields ? buildTable() : <Spinner />}
      {buildWorkingOverlay()}
    </Box>
  );

  function buildWorkingOverlay() {
    if (!isWorking) return null;

    return (
      <Box
        sx={{
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 4,
        }}
      >
        <Spinner />
      </Box>
    );
  }

  function fieldNames() {
    if (selectedFields.length === 1) return selectedFields[0].fieldName;
    return `${selectedFields.length} fields`;
  }

  function buildRejectConfirmation() {
    if (!isConfirmingReject) return null;

    return (
      <ConfirmationDialog
        title={`Reject Pending Soil Points for ${fieldNames()}?`}
        message={`Are you sure you want to reject the pending soil points for ${fieldNames()}?`}
        handleClose={() => setIsConfirmingReject(false)}
        handleConfirm={() => rejectFields()}
      />
    );
  }

  function buildApproveConfirmation() {
    if (!isConfirmingApprove) return null;

    return (
      <ConfirmationDialog
        title={`Approve Pending Soil Points for ${fieldNames()}?`}
        titleIcon={false}
        message={`Are you sure you want to approve the pending soil points for ${fieldNames()}?`}
        handleClose={() => setIsConfirmingApprove(false)}
        handleConfirm={() => approveFields()}
      />
    );
  }

  function buildHeader() {
    const disabled = !selectedFields.length;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography sx={{ display: 'inline-block', flex: 1 }} variant="h5">
          Pending Fields
        </Typography>
        <ActionButton>
          <Button onClick={downloadFields} disabled={disabled}>
            Download
          </Button>
          <Button onClick={confirmReject} disabled={disabled}>
            Reject
          </Button>
          <Button onClick={confirmApprove} disabled={disabled}>
            Approve
          </Button>
        </ActionButton>
      </Box>
    );
  }

  function buildTable() {
    return (
      <Table
        hideFooter={false}
        apiRef={apiRef}
        isRowSelectable={({ row }) => !row.isRunning}
        checkboxSelection
        rowSelectionModel={selectedFields.map(({ id }) => id)}
        onRowSelectionModelChange={setSelectedFields}
        disableRowSelectionOnClick
        columns={buildColumns()}
        rows={fields}
        getDetailPanelContent={getDetailPanelContent}
        getCellClassName={({ field, row }) =>
          field === 'receivedCount' &&
          row.soilPoints.length / row.targetCount <= 0.92
            ? 'error'
            : ''
        }
        slots={{
          noRowsOverlay: () => (
            <Stack height="100%" alignItems="center" justifyContent="center">
              There are no fields that currently need reviewing.
            </Stack>
          ),
        }}
        initialState={{
          sorting: {
            sortModel: [{ field: 'collectionDate', sort: 'desc' }],
          },
        }}
        sx={{
          boxShadow: 4,
          borderRadius: '5px',

          '& .MuiDataGrid-detailPanel': {
            p: 1,
            borderBottom: '1px solid #e0e0e0',
          },

          '& .MuiDataGrid-columnHeaders': {
            borderTopRightRadius: '5px',
            borderTopLeftRadius: '5px',
          },

          '& .error': {
            backgroundColor: '#f77e7e',
          },

          '& .warning': {
            backgroundColor: '#ffff33',
          },
        }}
      />
    );
  }

  function buildColumns() {
    return [
      {
        field: 'fieldId',
        headerName: 'Field ID',
        width: 70,
      },
      {
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        type: 'string',
        renderHeader: () => <div className="MuiDataGrid-columnHeaderTitle" />,
        renderCell: ({ id, value }) => (
          <TableDetailToggle id={id} value={value} />
        ),
      },
      {
        field: 'fieldName',
        headerName: 'Field Name',
        flex: 1,
      },
      {
        field: 'collectionDate',
        headerName: 'Collection Date',
        flex: 1,
        type: 'date',
        valueGetter: ({ row }) =>
          row.soilPoints
            .map((p) => p.collectionDate)
            .sort()
            .at(-1),
        valueFormatter: ({ value }) =>
          value &&
          new Intl.DateTimeFormat('en-US', { timeZone: 'UTC' }).format(
            new Date(value),
          ),
      },
      {
        field: 'organizationName',
        headerName: 'Organization Name',
        flex: 1,
      },
      {
        field: 'operationName',
        headerName: 'Operation Name',
        flex: 1,
      },
      {
        field: 'source',
        headerName: 'Source',
        flex: 0.75,
        valueGetter: ({ row }) =>
          row.soilPoints
            .map((p) => p.source)
            .sort()
            .at(-1),
      },
      {
        field: 'qcFlags',
        headerName: 'QC Flags',
        flex: 1.5,
        valueGetter: ({ row }) =>
          row.soilPoints
            .reduce(
              (all, point) => uniq([...all, ...Object.keys(point.qcFlags)]),
              [],
            )
            .map((key) => attributes[key].header)
            .sort()
            .join(', '),
      },
      {
        field: 'receivedCount',
        headerName: 'Received Count',
        flex: 0.5,
        type: 'number',
        valueGetter: ({ row }) => row.soilPoints.length,
      },
      {
        field: 'targetCount',
        headerName: 'Target Count',
        flex: 0.5,
        type: 'number',
      },
      {
        field: 'workOrderId',
        headerName: 'Work Order',
        flex: 0.5,
        type: 'number',
        valueFormatter: ({ value }) => (value ? `${value}` : ''),
      },
      {
        field: 'delayReasonId',
        headerName: 'Delay',
        width: 130,
        valueGetter: ({ row }) => row.delayReasonId,
        renderCell: ({ row }) => buildDelayReasonSelector(row),
      },
    ].filter(Boolean);
  }

  function buildDelayReasonSelector(row) {
    return (
      <Box className="MuiDataGrid-cellContent" sx={{ width: '100%' }}>
        <SelectButton
          sx={{
            '& .MuiButton-root': {
              justifyContent: 'left',
              px: 0,
            },
          }}
          onChange={updateDelayReason.bind(null, row.aaid)}
          value={row.delayReasonId}
          options={delayReasons}
        />
      </Box>
    );
  }

  function updateDelayReason(aaid, reason) {
    dispatch(actions.updateDelayReason(aaid, reason));
  }

  function setSelectedFields(ids) {
    dispatch(actions.setSelectedFields(ids));
  }

  function confirmApprove() {
    setIsConfirmingApprove(true);
  }

  function approveFields() {
    setIsConfirmingApprove(false);
    const expanded = apiRef.current.getExpandedDetailPanels();
    selectedFields
      .filter((field) => expanded.includes(field.id))
      .forEach((expandedField) =>
        apiRef.current.toggleDetailPanel(expandedField.id),
      );

    dispatch(actions.approveFields(selectedFields));
  }

  function confirmReject() {
    setIsConfirmingReject(true);
  }

  function rejectFields() {
    setIsConfirmingReject(false);
    dispatch(actions.rejectFields(selectedFields));
  }

  function downloadFields() {
    selectedFields.forEach((field) => {
      generateCsvDownload(
        field.soilPoints.map(({ delayReasonId, qcFlags, ...rest }) => ({
          operationName: field.operationName,
          fieldName: field.fieldName,
          aaid: field.aaid,
          ...rest,
        })),
        `sdacs-export-${field.aaid}.csv`,
      );
    });
  }
}
